import { useCallback, useEffect, useState } from 'react';
import { actorsAPI } from 'services/swagger-api';
import { FileTypeEnum, PersonDto, StoredFileIdResponse } from 'api/actor';

export type PersonType = 'physique' | 'morale';

export interface PersonDocuments {
  cni?: StoredFileIdResponse;
  rib?: StoredFileIdResponse;
  kbis?: StoredFileIdResponse;
}

export interface PersonWithDocumentsData {
  person: PersonDto | null;
  personType: PersonType | null;
  documents: PersonDocuments;
  memberDocuments: { [personneId: string]: StoredFileIdResponse[] };
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export const useFetchPersonWithDocuments = (personId: string): PersonWithDocumentsData => {
  const [person, setPerson] = useState<PersonDto | null>(null);
  const [personType, setPersonType] = useState<PersonType | null>(null);
  const [documents, setDocuments] = useState<PersonDocuments>({});
  const [memberDocuments, setMemberDocuments] = useState<{ [personneId: string]: StoredFileIdResponse[] }>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!personId) return;

    setIsLoading(true);
    try {
      const result = await actorsAPI.getPersonByID({
        personId,
        includeDocuments: true
      });

      if (!result) return;

      setPerson(result.person);
      setPersonType(result.person.type as PersonType);

      const docs = result.documents ?? [];
      setDocuments({
        cni: docs.find(d => d.fileType === FileTypeEnum.PieceIdentite),
        rib: docs.find(d => d.fileType === FileTypeEnum.Rib),
        kbis: docs.find(d => d.fileType === FileTypeEnum.Kbis),
      });

      setMemberDocuments(result.collaboratorDocuments ?? {});
    } catch (error) {
      console.error('Error fetching person with documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { person, personType, documents, memberDocuments, isLoading, refetch: fetchData };
};
